/**
 * 3D Configurator V3.2 - Robust Version
 * Completely rewritten 3D handling
 */

(function() {
  // Prevent double init
  if (window.Cfg3Loaded) return;
  window.Cfg3Loaded = true;

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(init, 100));
  } else {
    setTimeout(init, 100);
  }

  // Global state
  let THREE, scene, camera, renderer, controls, model;
  let meshes = new Map();
  let compMap = new Map();
  let cfg = null;
  let basePrice = 0;
  let startCamPos = null;
  let startCamTarget = null;

  async function init() {
    // Get config
    const configEl = document.getElementById('cfg3-config');
    if (!configEl) {
      console.error('Cfg3: No config found');
      return;
    }
    
    try {
      cfg = JSON.parse(configEl.textContent);
      console.log('Cfg3: Config', cfg);
    } catch (e) {
      console.error('Cfg3: Config parse error', e);
      return;
    }
    
    basePrice = cfg.basePrice || 0;
    
    // Setup UI first
    setupCards();
    setupVariants();
    setupColors();
    setupSliders();
    setupForm();
    
    // Then 3D
    const ok = await setup3D();
    if (ok && cfg.glbUrl) {
      await loadModel(cfg.glbUrl);
    } else {
      hideLoader();
      console.log('Cfg3: No GLB URL or 3D setup failed');
    }
  }

  async function setup3D() {
    const container = document.getElementById('cfg3-container');
    const canvas = document.getElementById('cfg3-canvas');
    
    if (!container || !canvas) {
      console.error('Cfg3: No container or canvas');
      return false;
    }
    
    try {
      // Import Three.js
      THREE = await import('three');
      const { OrbitControls } = await import('three/addons/controls/OrbitControls.js');
      
      const w = container.clientWidth || 400;
      const h = container.clientHeight || 500;
      
      console.log('Cfg3: Container size', w, 'x', h);
      
      // Scene
      scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf5f5f5);
      
      // Camera - use very wide near/far range
      camera = new THREE.PerspectiveCamera(45, w / h, 0.001, 10000);
      const camZ = cfg.camera?.z || 5;
      const camY = cfg.camera?.y || 0.5;
      const camX = cfg.camera?.x || 0;
      camera.position.set(camX, camY, camZ);
      
      // Renderer
      renderer = new THREE.WebGLRenderer({ 
        canvas: canvas,
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance'
      });
      renderer.setSize(w, h);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(0xf5f5f5, 1);
      
      // Color space
      if (THREE.SRGBColorSpace) {
        renderer.outputColorSpace = THREE.SRGBColorSpace;
      }
      
      // Controls
      controls = new OrbitControls(camera, canvas);
      controls.enableDamping = true;
      controls.dampingFactor = 0.1;
      controls.minDistance = 0.3;  // Allow very close zoom
      controls.maxDistance = 20;
      controls.target.set(0, 0, 0);
      controls.update();
      
      // Lights - intensity from config
      const lightInt = cfg.lightIntensity || 1.0;
      
      const ambient = new THREE.AmbientLight(0xffffff, 1.2 * lightInt);
      scene.add(ambient);
      
      const light1 = new THREE.DirectionalLight(0xffffff, 1.0 * lightInt);
      light1.position.set(5, 10, 7);
      scene.add(light1);
      
      const light2 = new THREE.DirectionalLight(0xffffff, 0.8 * lightInt);
      light2.position.set(-5, 5, -5);
      scene.add(light2);
      
      const light3 = new THREE.DirectionalLight(0xffffff, 0.5 * lightInt);
      light3.position.set(0, -5, 0);
      scene.add(light3);
      
      console.log('Cfg3: Light intensity', lightInt);
      
      // Debug: Add helper objects
      if (cfg.debug) {
        const axes = new THREE.AxesHelper(2);
        scene.add(axes);
      }
      
      // Handle resize - use both window resize and ResizeObserver for container
      let resizeTimer;
      const handleResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(onResize, 50);
      };
      
      window.addEventListener('resize', handleResize);
      
      // ResizeObserver for container changes (catches grid layout changes)
      if (typeof ResizeObserver !== 'undefined') {
        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(container);
        console.log('Cfg3: ResizeObserver attached');
      }
      
      // Initial resize after a short delay to ensure layout is complete
      setTimeout(onResize, 100);
      setTimeout(onResize, 500);
      
      // Reset button
      const resetBtn = document.getElementById('cfg3-reset');
      if (resetBtn) {
        resetBtn.addEventListener('click', resetCamera);
      }
      
      // Start render loop
      animate();
      
      console.log('Cfg3: 3D setup complete');
      return true;
      
    } catch (e) {
      console.error('Cfg3: 3D setup error', e);
      return false;
    }
  }

  async function loadModel(url) {
    console.log('Cfg3: Loading model from', url);
    
    try {
      const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
      const loader = new GLTFLoader();
      
      // Try to add Draco support
      try {
        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
        const draco = new DRACOLoader();
        draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
        loader.setDRACOLoader(draco);
        console.log('Cfg3: Draco enabled');
      } catch (e) {
        console.log('Cfg3: Draco not available');
      }
      
      // Load
      const gltf = await new Promise((resolve, reject) => {
        loader.load(
          url,
          resolve,
          (p) => {
            if (p.total > 0) console.log('Cfg3: Loading', Math.round(p.loaded/p.total*100) + '%');
          },
          reject
        );
      });
      
      model = gltf.scene;
      console.log('Cfg3: Model loaded');
      
      // Collect meshes
      model.traverse((node) => {
        if (node.isMesh) {
          meshes.set(node.name, node);
          // Store original material for color changes
          if (node.material) {
            node.userData.originalMaterial = node.material.clone();
          }
        }
      });
      
      console.log('Cfg3: Found', meshes.size, 'meshes');
      
      // Show debug info
      if (cfg.debug) {
        showMeshDebug();
      }
      
      // Calculate bounds BEFORE any transforms
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      
      console.log('Cfg3: Original size', size.x.toFixed(2), size.y.toFixed(2), size.z.toFixed(2));
      console.log('Cfg3: Original center', center.x.toFixed(2), center.y.toFixed(2), center.z.toFixed(2));
      
      // Scale model to reasonable size FIRST (fit in ~3 units)
      const maxDim = Math.max(size.x, size.y, size.z);
      const targetSize = 3;
      const scaleFactor = targetSize / maxDim;
      model.scale.setScalar(scaleFactor);
      
      console.log('Cfg3: Scale factor', scaleFactor.toFixed(4));
      
      // Recalculate bounds AFTER scaling
      const scaledBox = new THREE.Box3().setFromObject(model);
      const scaledCenter = scaledBox.getCenter(new THREE.Vector3());
      
      console.log('Cfg3: Scaled center', scaledCenter.x.toFixed(2), scaledCenter.y.toFixed(2), scaledCenter.z.toFixed(2));
      
      // Now move model so its CENTER is at origin (0,0,0)
      model.position.set(-scaledCenter.x, -scaledCenter.y, -scaledCenter.z);
      
      // Apply model rotation from settings (convert degrees to radians)
      const rot = cfg.modelRotation || { x: 0, y: 0, z: 0 };
      const toRad = (deg) => deg * Math.PI / 180;
      
      // Create a container group for rotation
      const modelContainer = new THREE.Group();
      modelContainer.add(model);
      modelContainer.rotation.set(toRad(rot.x), toRad(rot.y), toRad(rot.z));
      
      console.log('Cfg3: Model rotation', rot.x + '°', rot.y + '°', rot.z + '°');
      
      // Add container to scene (not model directly)
      scene.add(modelContainer);
      console.log('Cfg3: Model added to scene');
      
      // Verify final position
      const finalBox = new THREE.Box3().setFromObject(modelContainer);
      const finalCenter = finalBox.getCenter(new THREE.Vector3());
      console.log('Cfg3: Final center', finalCenter.x.toFixed(2), finalCenter.y.toFixed(2), finalCenter.z.toFixed(2));
      
      // Set camera to view the model using settings from config
      const camCfg = cfg.camera || { distance: 4, height: 0, angle: 0 };
      const camSetup = calcCameraPosition(camCfg.distance, camCfg.height, camCfg.angle, 0);
      
      camera.position.copy(camSetup.position);
      controls.target.copy(camSetup.target);
      controls.update();
      
      // Store for reset
      startCamPos = camera.position.clone();
      startCamTarget = controls.target.clone();
      
      console.log('Cfg3: Camera at', camera.position.x.toFixed(2), camera.position.y.toFixed(2), camera.position.z.toFixed(2));
      
      // Map components to meshes
      mapComponents();
      
      // Hide permanently hidden meshes first
      hidePermamentMeshes();
      
      // Apply initial selections (variants first, then colors)
      applyInitialVariants();
      applyInitialColors();
      
      // Force a render
      renderer.render(scene, camera);
      
      // Hide loader
      hideLoader();
      
      // Trigger resize to ensure canvas fits container
      setTimeout(onResize, 100);
      
      console.log('Cfg3: Ready!');
      
    } catch (e) {
      console.error('Cfg3: Model load error', e);
      hideLoader();
    }
  }

  function showMeshDebug() {
    const list = document.getElementById('cfg3-meshlist');
    if (!list) return;
    
    list.innerHTML = '';
    meshes.forEach((mesh, name) => {
      const span = document.createElement('span');
      span.textContent = name;
      span.style.cursor = 'pointer';
      span.onclick = () => {
        navigator.clipboard?.writeText(name);
        span.style.background = 'lime';
        setTimeout(() => span.style.background = '', 300);
      };
      list.appendChild(span);
    });
  }

  function mapComponents() {
    if (!cfg.components) return;
    
    cfg.components.forEach(comp => {
      const searchNames = (comp.meshes || [])
        .map(n => String(n).trim().toLowerCase())
        .filter(n => n.length > 0);
      
      const matched = [];
      
      meshes.forEach((mesh, meshName) => {
        const lowerMeshName = meshName.toLowerCase();
        
        for (const searchName of searchNames) {
          let isMatch = false;
          
          // Exact match
          if (lowerMeshName === searchName) {
            isMatch = true;
          }
          
          // Match with common suffixes (Links, Rechts, Left, Right, etc.)
          if (!isMatch) {
            const suffixes = ['links', 'rechts', 'left', 'right', '_l', '_r', '_left', '_right', '001', '002', '01', '02', '1', '2'];
            for (const suffix of suffixes) {
              if (lowerMeshName === searchName + suffix || lowerMeshName === searchName + '_' + suffix) {
                isMatch = true;
                break;
              }
            }
          }
          
          if (isMatch) {
            matched.push(mesh);
            console.log('Cfg3: Component "' + comp.title + '" matched mesh "' + meshName + '"');
            break;
          }
        }
      });
      
      compMap.set(comp.id, { ...comp, matchedMeshes: matched });
      console.log('Cfg3: Component "' + comp.title + '" total:', matched.length, 'meshes');
    });
  }

  function applyInitialColors() {
    document.querySelectorAll('.cfg3-panel').forEach(panel => {
      const compId = panel.dataset.panelId;
      const activeColor = panel.querySelector('.cfg3-color--active');
      if (activeColor && compId) {
        const hex = activeColor.dataset.colorHex;
        if (hex) applyColor(compId, hex);
      }
    });
  }

  function applyInitialVariants() {
    // Apply all initially selected variants (mesh visibility)
    document.querySelectorAll('.cfg3-var-btn--active').forEach(btn => {
      const show = btn.dataset.show;
      const hide = btn.dataset.hide;
      
      if (hide) {
        console.log('Cfg3: Initial hide meshes:', hide);
        setMeshVisibility(hide, false);
      }
      if (show) {
        console.log('Cfg3: Initial show meshes:', show);
        setMeshVisibility(show, true);
      }
    });
  }

  function hidePermamentMeshes() {
    // Hide meshes defined in section settings
    const hidden = cfg.hiddenMeshes;
    if (hidden && hidden.trim()) {
      console.log('Cfg3: Permanently hiding meshes:', hidden);
      setMeshVisibility(hidden, false);
    }
  }

  function hideLoader() {
    const el = document.getElementById('cfg3-loading');
    if (el) el.classList.add('cfg3-hide');
  }

  function animate() {
    requestAnimationFrame(animate);
    if (controls) controls.update();
    if (renderer && scene && camera) {
      renderer.render(scene, camera);
    }
  }

  function onResize() {
    const container = document.getElementById('cfg3-container');
    if (!container || !camera || !renderer) return;
    
    const w = container.clientWidth;
    const h = container.clientHeight;
    
    console.log('Cfg3: Resize to', w, 'x', h);
    
    if (w > 0 && h > 0) {
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, true);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      
      // Force a render
      if (scene) {
        renderer.render(scene, camera);
      }
    }
  }

  function resetCamera() {
    if (startCamPos && startCamTarget && camera && controls) {
      animateCamera(startCamPos.clone(), startCamTarget.clone());
    }
  }

  // Calculate camera position from intuitive values
  function calcCameraPosition(distance, height, angleDeg, focusY) {
    const angleRad = (angleDeg || 0) * Math.PI / 180;
    
    // Camera orbits around Y axis at given distance and height
    const x = Math.sin(angleRad) * distance;
    const y = height || 0;
    const z = Math.cos(angleRad) * distance;
    
    // Focus point (where camera looks at)
    const targetY = focusY || 0;
    
    return {
      position: new THREE.Vector3(x, y, z),
      target: new THREE.Vector3(0, targetY, 0)
    };
  }

  function animateCamera(toPos, toTarget) {
    const fromPos = camera.position.clone();
    const fromTarget = controls.target.clone();
    const duration = 400;
    const start = performance.now();
    
    function tick(now) {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3); // ease out cubic
      
      camera.position.lerpVectors(fromPos, toPos, ease);
      controls.target.lerpVectors(fromTarget, toTarget, ease);
      controls.update();
      
      if (t < 1) requestAnimationFrame(tick);
    }
    
    requestAnimationFrame(tick);
  }

  // === Color Application ===
  function applyColor(compId, hexColor) {
    const comp = compMap.get(compId);
    if (!comp || !comp.matchedMeshes || comp.matchedMeshes.length === 0) {
      console.log('Cfg3: No meshes for component', compId);
      return;
    }
    
    if (!THREE) return;
    
    const color = new THREE.Color(hexColor);
    console.log('Cfg3: Applying', hexColor, 'to', comp.matchedMeshes.length, 'meshes');
    
    comp.matchedMeshes.forEach(mesh => {
      if (!mesh.material) return;
      
      // Clone material if not already done
      if (!mesh.userData.materialCloned) {
        mesh.material = mesh.material.clone();
        mesh.userData.materialCloned = true;
      }
      
      const mat = mesh.material;
      
      // Set the color directly (replaces, not mixes)
      if (mat.color) {
        mat.color.setHex(color.getHex());
      }
      
      // For PBR materials, also adjust these properties for better color display
      if (mat.isMeshStandardMaterial || mat.isMeshPhysicalMaterial) {
        // Remove any color map that might interfere
        if (mat.map) {
          mat.map = null;
        }
        // Set emissive to black (no glow)
        if (mat.emissive) {
          mat.emissive.setHex(0x000000);
        }
        // Reduce metalness for better color visibility
        if (mat.metalness !== undefined && mat.metalness > 0.5) {
          mat.metalness = 0.3;
        }
        // Increase roughness slightly for better color
        if (mat.roughness !== undefined && mat.roughness < 0.3) {
          mat.roughness = 0.4;
        }
      }
      
      // For basic materials
      if (mat.isMeshBasicMaterial || mat.isMeshLambertMaterial || mat.isMeshPhongMaterial) {
        if (mat.map) {
          mat.map = null;
        }
      }
      
      mat.needsUpdate = true;
    });
  }

  // === Mesh Visibility ===
  function setMeshVisibility(namesStr, visible) {
    if (!namesStr) return;
    
    const names = namesStr.split(',').map(n => n.trim().toLowerCase()).filter(n => n);
    if (names.length === 0) return;
    
    meshes.forEach((mesh, meshName) => {
      const lowerMeshName = meshName.toLowerCase();
      
      for (const searchName of names) {
        // Exact match
        if (lowerMeshName === searchName) {
          mesh.visible = visible;
          console.log('Cfg3: Mesh', meshName, visible ? 'shown' : 'hidden', '(exact match)');
          break;
        }
        
        // Match with common suffixes (Links, Rechts, Left, Right, _L, _R, etc.)
        const suffixes = ['links', 'rechts', 'left', 'right', '_l', '_r', '_left', '_right', '001', '002', '01', '02', '1', '2'];
        for (const suffix of suffixes) {
          if (lowerMeshName === searchName + suffix || lowerMeshName === searchName + '_' + suffix) {
            mesh.visible = visible;
            console.log('Cfg3: Mesh', meshName, visible ? 'shown' : 'hidden', '(suffix match)');
            break;
          }
        }
      }
    });
  }

  // === UI Setup ===
  function setupCards() {
    const cards = document.querySelectorAll('.cfg3-card');
    const panels = document.querySelectorAll('.cfg3-panel');
    const selection = document.querySelector('.cfg3-selection');
    const intro = document.getElementById('cfg3-intro');
    
    cards.forEach(card => {
      card.addEventListener('click', () => {
        const id = card.dataset.compId;
        
        // Show selection container
        if (selection) {
          selection.classList.remove('cfg3-selection--hidden');
          selection.classList.add('cfg3-selection--visible');
        }
        
        // Hide intro box
        if (intro) {
          intro.classList.add('cfg3-intro--hidden');
        }
        
        // Update active states
        cards.forEach(c => c.classList.remove('cfg3-card--active'));
        card.classList.add('cfg3-card--active');
        
        panels.forEach(p => p.classList.remove('cfg3-panel--active'));
        const panel = document.querySelector('[data-panel-id="' + id + '"]');
        if (panel) panel.classList.add('cfg3-panel--active');
        
        // Get camera settings
        const distance = parseFloat(card.dataset.camDistance) || 4;
        const height = parseFloat(card.dataset.camHeight) || 0;
        const angle = parseFloat(card.dataset.camAngle) || 0;
        const focusY = parseFloat(card.dataset.focusY) || 0;
        
        console.log('Cfg3: Camera - distance:', distance, 'height:', height, 'angle:', angle, 'focusY:', focusY);
        
        if (camera && controls && THREE) {
          const camSetup = calcCameraPosition(distance, height, angle, focusY);
          animateCamera(camSetup.position, camSetup.target);
        }
      });
    });
  }

  function setupVariants() {
    document.querySelectorAll('.cfg3-variants').forEach(container => {
      const btns = container.querySelectorAll('.cfg3-var-btn');
      const panel = container.closest('.cfg3-panel');
      const colorsContainer = panel?.querySelector('.cfg3-colors');
      
      btns.forEach(btn => {
        btn.addEventListener('click', () => {
          const variant = btn.dataset.variant;
          const show = btn.dataset.show;
          const hide = btn.dataset.hide;
          
          // Update active
          btns.forEach(b => b.classList.remove('cfg3-var-btn--active'));
          btn.classList.add('cfg3-var-btn--active');
          
          // First reset visibility
          btns.forEach(b => {
            if (b.dataset.hide) setMeshVisibility(b.dataset.hide, true);
          });
          
          // Then apply current
          if (hide) setMeshVisibility(hide, false);
          if (show) setMeshVisibility(show, true);
          
          // Switch color images
          if (colorsContainer) {
            colorsContainer.querySelectorAll('.cfg3-color__img img').forEach(img => {
              img.classList.remove('cfg3-color__img--show');
              if (img.dataset.variant === variant) {
                img.classList.add('cfg3-color__img--show');
              }
            });
          }
          
          updatePrice();
          updateProperties();
          updateVariantId();
        });
      });
      
      // Init: show variant 1 images
      if (colorsContainer) {
        colorsContainer.querySelectorAll('.cfg3-color__img img[data-variant="1"]').forEach(img => {
          img.classList.add('cfg3-color__img--show');
        });
      }
    });
  }

  function setupColors() {
    document.querySelectorAll('.cfg3-colors').forEach(container => {
      const colors = container.querySelectorAll('.cfg3-color');
      const compId = container.dataset.compId;
      
      colors.forEach(col => {
        col.addEventListener('click', () => {
          colors.forEach(c => c.classList.remove('cfg3-color--active'));
          col.classList.add('cfg3-color--active');
          
          const hex = col.dataset.colorHex;
          if (hex && compId) {
            applyColor(compId, hex);
          }
          
          updatePrice();
          updateProperties();
        });
      });
    });
  }

  function setupSliders() {
    document.querySelectorAll('.cfg3-slider').forEach(slider => {
      const valEl = slider.closest('.cfg3-slider-box')?.querySelector('[data-slider-val]');
      const unit = slider.dataset.unit || '';
      
      slider.addEventListener('input', () => {
        if (valEl) valEl.textContent = slider.value + unit;
        updateProperties();
      });
    });
  }

  function setupForm() {
    const form = document.getElementById('cfg3-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        updateProperties();
        updateVariantId();
      });
    }
  }

  function updateVariantId() {
    // Get current selections
    const selections = {};
    document.querySelectorAll('.cfg3-panel').forEach(panel => {
      const title = panel.querySelector('.cfg3-panel__title')?.textContent?.trim();
      const activeVar = panel.querySelector('.cfg3-var-btn--active');
      if (title && activeVar) {
        // Get variant name without price suffix
        let varName = activeVar.textContent.trim();
        const priceIdx = varName.indexOf('+');
        if (priceIdx > 0) varName = varName.substring(0, priceIdx).trim();
        selections[title] = varName;
      }
    });
    
    console.log('Cfg3: Current selections', selections);
    
    // Find matching variant rule
    const rules = cfg.variantRules || [];
    let matchedVariantId = null;
    
    for (const rule of rules) {
      if (!rule.conditions || !rule.variantId) continue;
      
      // Parse conditions: "Griffe=Ohne Schlaufe,Teller=Piste"
      const conditions = rule.conditions.split(',').map(c => c.trim());
      let allMatch = true;
      
      for (const cond of conditions) {
        const [compName, varName] = cond.split('=').map(s => s.trim());
        if (!compName || !varName) continue;
        
        // Check if selection matches (case insensitive, partial match)
        const selectedVar = selections[compName];
        if (!selectedVar || !selectedVar.toLowerCase().includes(varName.toLowerCase())) {
          allMatch = false;
          break;
        }
      }
      
      if (allMatch) {
        matchedVariantId = rule.variantId;
        console.log('Cfg3: Matched variant rule', rule.conditions, '-> ID:', matchedVariantId);
        break;
      }
    }
    
    // Update hidden variant ID field
    if (matchedVariantId) {
      const idInput = document.querySelector('#cfg3-form input[name="id"]');
      if (idInput) {
        idInput.value = matchedVariantId;
        console.log('Cfg3: Set variant ID to', matchedVariantId);
      }
    }
  }

  // === Price ===
  function updatePrice() {
    let adjust = 0;
    
    document.querySelectorAll('.cfg3-var-btn--active').forEach(btn => {
      adjust += (parseFloat(btn.dataset.price) || 0) * 100;
    });
    
    document.querySelectorAll('.cfg3-color--active').forEach(col => {
      adjust += (parseFloat(col.dataset.price) || 0) * 100;
    });
    
    const total = basePrice + adjust;
    const formatted = formatMoney(total);
    
    // Update ALL price displays (both mobile and desktop headers)
    document.querySelectorAll('[data-price-display]').forEach(el => {
      el.textContent = formatted;
    });
    
    // Update buy button price
    const buyEl = document.querySelector('[data-buy-price]');
    if (buyEl) buyEl.textContent = formatted;
  }

  function formatMoney(cents) {
    const amt = (cents / 100).toFixed(2);
    const amtComma = amt.replace('.', ',');
    const fmt = cfg?.moneyFormat || '€{{amount_with_comma_separator}}';
    
    return fmt
      .replace('{{amount}}', amt)
      .replace('{{amount_with_comma_separator}}', amtComma)
      .replace('{{amount_no_decimals}}', Math.floor(cents / 100));
  }

  // === Properties ===
  function updateProperties() {
    const container = document.getElementById('cfg3-props');
    if (!container) return;
    
    container.innerHTML = '';
    
    document.querySelectorAll('.cfg3-panel').forEach(panel => {
      const title = panel.querySelector('.cfg3-panel__title')?.textContent?.trim() || 'Option';
      
      // Variant
      const varBtn = panel.querySelector('.cfg3-var-btn--active');
      if (varBtn) {
        addProp(container, title, varBtn.textContent.trim().split('+')[0].trim());
      }
      
      // Color
      const colBtn = panel.querySelector('.cfg3-color--active');
      if (colBtn?.dataset.colorName) {
        addProp(container, title + ' Farbe', colBtn.dataset.colorName);
      }
      
      // Slider
      const slider = panel.querySelector('.cfg3-slider');
      if (slider) {
        const label = panel.querySelector('.cfg3-label')?.textContent?.trim() || 'Größe';
        addProp(container, label, slider.value + (slider.dataset.unit || ''));
      }
    });
  }

  function addProp(container, name, value) {
    if (!value) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[' + name + ']';
    input.value = value;
    container.appendChild(input);
  }

})();
